import { useTranslation } from 'react-i18next';
import { ViewState } from './ViewState.jsx';
import { classifyError } from './status.js';

/**
 * The recovery surface for a failed operation (spec §11.1).
 *
 * A view hands it whatever the request threw; the failure is classified into
 * the right mandated state (offline / session / permission / no source / plain
 * error) so the user reads the *correct* explanation rather than a generic one,
 * and retrying resumes the same operation instead of resetting the screen.
 *
 * Render it *next to* the still-valid content (composer, draft, partial
 * answer) — never instead of it.
 *
 * @param {object} props
 * @param {unknown} props.error - the failure to explain.
 * @param {() => void} props.onRetry - re-runs the same operation.
 * @param {boolean} [props.retrying=false]
 * @param {number} [props.attempts=0] - shown from the second attempt on, so a
 *   user who is retrying into a wall can see it is not their fault.
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} [props.description]
 * @param {import('react').ReactNode} [props.action] - extra action (e.g. "Support kontaktieren").
 * @param {boolean} [props.compact=false]
 * @param {string} [props.className]
 */
export function RetryPanel({
  error,
  onRetry,
  retrying = false,
  attempts = 0,
  title,
  description,
  action,
  compact = false,
  className = '',
  ...rest
}) {
  const { t } = useTranslation();
  const status = classifyError(error);

  return (
    <ViewState
      status={status}
      title={title}
      description={description}
      error={error}
      onRetry={onRetry}
      onRefresh={onRetry}
      busy={retrying}
      compact={compact}
      className={className}
      action={
        <>
          {attempts > 1 && <p className="etch-state__meta">{t('states.attempts', { n: attempts })}</p>}
          {action}
        </>
      }
      {...rest}
    />
  );
}

export default RetryPanel;
